import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Inscription, Preloader, SearchInput, useDebounce } from '@common-fe/common-fe';

import { LockboxType,MatchedEmployee } from '@/modules/employer/components/Lockbox/lockbox.types';
import {
  LoadNextItemsButton,
  MatchingListEmptyState,
  TooManyResultsState,
} from '@/modules/employer/components/Lockbox/LockboxDetails/components/LockboxInfo/LockboxMatchingList';
import { useEmployeeCardsList } from '@/modules/employer/components/Lockbox/LockboxDetails/hooks';
import useLockboxDetailsStore from '@/modules/employer/components/Lockbox/LockboxDetails/stores/useLockboxDetails.store';
import useGetMatchedEmployeeByIdQuery from '@/modules/employer/components/Lockbox/queries/useGetMatchedEmployeeById.query';
import useGetMatchedEmployeesQuery from '@/modules/employer/components/Lockbox/queries/useGetMatchedEmployees.query';
import usePagination from '@/utils/hooks/usePagination';

import EmployeesList from './components/EmployeesList';
import { addInvestmentsWarning } from './addInvestmentsWarning';
import { getMatchedEmployeeById } from './getMatchedEmployeeById';

const MIN_SEARCH_QUERY_LENGTH = 3;

const EmployeesSearch = () => {
  const [searchString, setSearchString] = useState('');
  const debouncedSearchString = useDebounce(searchString, 1000);
  const { editableLockbox, originalLockbox, setEditableLockbox, isEditMode } = useLockboxDetailsStore();
  const { foundEmployees, isTooMany, isLoading } = useGetMatchedEmployeesQuery({ searchValue: debouncedSearchString });

  const defaultSearchEmployee = useMemo(() => {
    if (!originalLockbox?.employeeId) {
      return '';
    }

    const employeeFromMatchEmployees = getMatchedEmployeeById(originalLockbox?.employeeId, originalLockbox?.accountId, originalLockbox?.matchEmployees);

    if (employeeFromMatchEmployees) {
      return '';
    }

    return originalLockbox?.employeeName;
  }, [originalLockbox]);

  const { foundEmployee } = useGetMatchedEmployeeByIdQuery({
    employeeId: originalLockbox?.employeeId,
    accountId: originalLockbox?.accountId,
    enabled: !!defaultSearchEmployee,
  });

  const isEmployeeNameInSearch = useMemo(() => foundEmployee && foundEmployee?.employeeName === searchString,
    [foundEmployee, searchString]);

  const filteredEmployees = useMemo(() => {
    if (!foundEmployee || !foundEmployees || !isEmployeeNameInSearch) {
      return foundEmployees;
    }

    return foundEmployees.filter((employee) => employee.employeeId !== foundEmployee?.employeeId);
  }, [foundEmployee, foundEmployees, isEmployeeNameInSearch]);

  const allEmployees = useMemo(() => [
    ...(foundEmployee && isEmployeeNameInSearch ? [foundEmployee] : []),
    ...(filteredEmployees?.length ? filteredEmployees : [])
  ], [foundEmployee, filteredEmployees, isEmployeeNameInSearch]);
  
  const allEmployeesWithWarnings = useMemo(() => allEmployees
    .map((employee) => addInvestmentsWarning(employee, editableLockbox?.lockboxType === LockboxType.OUTBOUND_TOA)),
  [allEmployees, editableLockbox?.lockboxType]);

  const { fields } = useEmployeeCardsList({
    matchedEmployees: allEmployeesWithWarnings,
    lockboxType: editableLockbox?.lockboxType,
  });
  const { slicedItems, hasNextItems, handleNext, reset } = usePagination({ items: fields, initialSize: 5, stepSize: 5 });

  const handleSetEmployee = useCallback((employee?: MatchedEmployee) => {
    if (employee) {
      setEditableLockbox({
        employeeId: employee?.employeeId,
        employeeName: employee?.employeeName,
        accountId: employee?.accountId,
        employeeAccountStatus: employee?.accountStatus,
        employeeInvestmentsBalance: employee?.investmentsBalance,
        employeeAvailableAmount: employee?.availableAmount,
        employeeSsn: employee?.ssn,
        isEmployeeFromSearch: true,
      });
    }
  }, [setEditableLockbox]);
  const handleResetEmployee = useCallback(() => handleSetEmployee({
    isEmployeeFromSearch: undefined,
  } as MatchedEmployee), [handleSetEmployee]);
  useEffect(() => {
    if (!foundEmployees?.length) {
      handleResetEmployee();
    }
  }, [foundEmployees, handleResetEmployee]);

  const handleSelectEmployeeId = useCallback((employeeId?: string) => {
    const matchedEmployee = allEmployeesWithWarnings
      ?.find((foundEmployee) => foundEmployee?.employeeId === employeeId);

    handleSetEmployee(matchedEmployee);
  }, [handleSetEmployee, allEmployeesWithWarnings]);

  useEffect(() => {
    reset();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchString]);

  useEffect(() => {
    if (defaultSearchEmployee) {
      setSearchString(defaultSearchEmployee);
      setEditableLockbox({ isEmployeeFromSearch: true });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultSearchEmployee]);

  useEffect(() => {
    handleSetEmployee(foundEmployee);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [foundEmployee]);

  return (
    <Box data-testid="employees-search">
      <Box data-testid="employees-search_header" direction="row" justify="between" align="center">
        <Inscription color="textSecondary" whiteSpace="nowrap">
          Manual search:
        </Inscription>

        <Box margin={{ left: 'spacing12' }} width={{ max: '364px', width: '100%' }}>
          <SearchInput
            testId="employees-search"
            value={searchString}
            onChange={setSearchString}
            placeholder="Search for name, SSN, account number"
          />
        </Box>
      </Box>

      <Box margin={{ top: 'spacing8' }}>
        <EmployeesList
          fields={slicedItems}
          selectedEmployeeId={editableLockbox?.employeeId}
          onSelectEmployee={handleSelectEmployeeId}
          isEditMode={isEditMode}
          showWarnings
          {...debouncedSearchString.length >= MIN_SEARCH_QUERY_LENGTH ? {
            emptyState: (
              <Box margin={{ top: 'spacing8' }}>
                {isLoading ? (
                  <Preloader testId="employees-search_preloader" />
                ) : (
                  <MatchingListEmptyState testId="lockbox-employees" title="No Search Results" />
                )}
              </Box>
            )
          }: {}}
        />
      </Box>

      {!!slicedItems?.length && (
        <Box margin={{ top: 'spacing8' }} align="center">
          <LoadNextItemsButton
            hasNextItems={hasNextItems}
            onClick={handleNext}
          />
        </Box>
      )}

      {isTooMany && (
        <Box margin={{ top: 'spacing8' }}>
          <TooManyResultsState testId="employees-search" />
        </Box>
      )}
    </Box>
  );
};

export default EmployeesSearch;
